import { withTranslation } from 'react-i18next';
import numeral from 'numeral';

import DataTable from '../../../../components/Datatable';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { COVERAGE_COLUMNS } from '../keys';

/**
 * Block 04 — what the review covered, and the export of exactly that.
 *
 * Its job is to keep the queue above it in proportion. The candidate count means nothing without
 * the size of the set it was chosen from, and neither figure is the property's keyword coverage:
 * Search Console withholds rows below its privacy thresholds and stops returning them at its own
 * ceiling, so a retrieved universe is what was handed over, not what exists.
 *
 * Both presets are counted against the same retrieved rows so the other reading of the same data
 * is visible rather than implied, and the last row says in words what a candidate is — a query
 * worth a look, not a forecast gain.
 */
function ReviewCoverage({
    t,
    thresholds,
    retrievedRows,
    candidates,
    counts,
    exportable,
    onExport,
}) {
    const columns = COVERAGE_COLUMNS(t);
    const count = (value) =>
        value === null || value === undefined
            ? t('tools.gsc.not_returned')
            : numeral(value).format('0,0');

    const rows = [
        {
            id: 'retrieved',
            item: t('tools.search_opportunities.coverage.retrieved'),
            value: count(retrievedRows),
        },
        {
            id: 'nearer_top',
            item: t('tools.search_opportunities.coverage.nearer_top'),
            value: count(counts?.nearerTop),
        },
        {
            id: 'low_ctr',
            item: t('tools.search_opportunities.coverage.low_ctr', {
                max: numeral(thresholds.maxCtr).format('0.00%'),
            }),
            value: count(counts?.lowCtr),
        },
        {
            id: 'applied',
            item: t('tools.search_opportunities.coverage.applied', {
                preset: t(`tools.search_opportunities.presets.${thresholds.preset}`),
            }),
            value: count(candidates),
        },
        {
            id: 'meaning',
            item: t('tools.search_opportunities.coverage.meaning'),
            value: t('tools.search_opportunities.meaning'),
        },
    ];

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.search_opportunities.coverage.title')}</p>
                    <p className="gsc-tool-sub">
                        {t('tools.search_opportunities.coverage.description')}
                    </p>
                </div>

                <div className="gsc-tool-icon-actions">
                    <button
                        type="button"
                        title={t('tools.search_opportunities.coverage.export')}
                        aria-label={t('tools.search_opportunities.coverage.export')}
                        onClick={onExport}
                        disabled={!exportable}
                    >
                        <i className="icon-fi_download" />
                    </button>
                </div>
            </div>

            <div className="card-body">
                <DataTable
                    nodes={rows}
                    COLUMNS={columns}
                    sortFns={getSortKeys(columns)}
                    additionalStyles={setAdditionalStyles(columns)}
                    onlyTable
                    notUseDefaultStyle
                    customContainerStyles="gsc-tool-rules-table"
                />

                <p className="gsc-tool-table-note">
                    {t('tools.search_opportunities.coverage.not_total')}
                </p>
                <p className="gsc-tool-table-note">
                    {t('tools.search_opportunities.coverage.export_note')}
                </p>
            </div>
        </div>
    );
}

export default withTranslation()(ReviewCoverage);
